/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-22
 */

// Add third-party dependencies.
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import ContactForm from './ContactForm';
import toolsDanceLogo from '../../../assets/images/tools-dance-logo.svg';
import Header from '../../layout/Header';

/**
 * Contact page component with form and contact information.
 * @example
 * return (
 *   <Contact />
 * )
 * @returns {React.ReactElement} component.
 */
const Contact: React.FC = () => {
  const { t } = useTranslation('contact');
  const navigate = useNavigate();

  const handleHomeClick = () => {
    navigate('/');
  };

  // Commented out for MVP launch - keep for potential rollback
  // const handleWaitlistClick = () => {
  //   navigate('/waitlist');
  // };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      {/* Header */}
      <Header showContact={false} />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
              mb: 2,
            }}
          >
            {t('page.title')}
          </Typography>
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{
              maxWidth: 600,
              mx: 'auto',
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              lineHeight: 1.5,
            }}
          >
            {t('page.subtitle')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 6,
          }}
        >
          {/* Contact Form */}
          <Box sx={{ flex: { xs: 1, md: 2 } }}>
            <ContactForm />
          </Box>

          {/* Contact Information */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* About Us Card */}
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {t('about.title')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {t('about.paragraph1')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('about.paragraph2')}
                  </Typography>
                </CardContent>
              </Card>

              {/* Quick Links Card */}
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {t('quickLinks.title')}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleHomeClick}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {t('quickLinks.learnMore')}
                    </Button>
                    {/* Waitlist button commented out for MVP launch - keep for potential rollback */}
                    {/* <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleWaitlistClick}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                      }}
                    >
                      {t('quickLinks.joinWaitlist')}
                    </Button> */}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          bgcolor: 'background.paper',
          py: 6,
          mt: 8,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 4,
              alignItems: 'center',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src={toolsDanceLogo}
                  alt={t('footer.logoAlt')}
                  style={{ height: 24, marginRight: 12 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {t('footer.logoAlt')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('footer.tagline')}
              </Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="body2" color="text.secondary">
                {t('footer.copyright')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t('footer.motto')}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;
